import type { Maybe } from '@graphql/generated';
import type { LatLngLiteral } from '@shared/core/google-maps/types';

/**
 * Union type supporting all three backend coordinate formats:
 * - Room block (zh api): lat & lon
 * - Accommodation: lat & long
 * - Joy Place / Property: latitude & longitude
 *
 * This type allows any combination, so callers don't need to worry about exact property names.
 */
export type CoordinateFormats = Readonly<{
  latitude?: Maybe<number>;
  lat?: Maybe<number>;

  longitude?: Maybe<number>;
  lng?: Maybe<number>;
  lon?: Maybe<number>;
  long?: Maybe<number>;
}>;

/**
 * Converts any supported coordinate format to Google Maps LatLngLiteral format.
 * @param location - The location object in any of the supported formats.
 * @returns Google Maps coordinate object { lat, lng } or undefined if conversion fails.
 */
const convertToGoogleCoordinates = (location?: Maybe<CoordinateFormats>): LatLngLiteral | undefined => {
  if (!location) return undefined;

  const lat = location.latitude ?? location.lat;
  const lng = location.longitude ?? location.lng ?? location.lon ?? location.long;

  if (typeof lat !== 'number' || typeof lng !== 'number') {
    return undefined;
  }

  return { lat, lng };
};

/**
 * Checks if LatLngLiteral coordinates are within valid geographic ranges.
 * @param coords - The coordinates to validate.
 * @returns True if within valid ranges (latitude: -90 to 90, longitude: -180 to 180).
 */
const isWithinValidRange = (coords: LatLngLiteral): boolean => coords.lat >= -90 && coords.lat <= 90 && coords.lng >= -180 && coords.lng <= 180;

/**
 * Type guard to check if the provided location has valid numeric latitude and longitude values within valid ranges.
 *
 * @param location - The location object to validate (supports latitude/longitude format).
 * @returns True if latitude and longitude are both numbers and within valid ranges (latitude: -90 to 90, longitude: -180 to 180).
 */
export function isValidCoordinates(location?: Maybe<{ latitude?: Maybe<number>; longitude?: Maybe<number> }>): location is { latitude: number; longitude: number };

/**
 * Checks if the provided location has valid numeric coordinate values within valid ranges.
 *
 * @param location - The location object to validate in any of the supported formats.
 * @returns True if coordinates are both numbers and within valid ranges.
 */
export function isValidCoordinates(location?: Maybe<CoordinateFormats>): boolean;
export function isValidCoordinates(location?: Maybe<CoordinateFormats>): boolean {
  const coords = convertToGoogleCoordinates(location);
  return !!coords && isWithinValidRange(coords);
}

/**
 * Converts a location object with optional coordinates into a valid google.maps.LatLngLiteral,
 * or returns undefined if the coordinates are invalid.
 *
 * @param location - The input location object in any of the supported formats.
 * @returns A valid google.maps.LatLngLiteral coordinate object or undefined if invalid.
 */
export const getValidCoordinates = (location?: Maybe<CoordinateFormats>): LatLngLiteral | undefined => {
  const coords = convertToGoogleCoordinates(location);
  if (!coords || !isWithinValidRange(coords)) return undefined;

  return coords;
};
